// Affiliate referral capture.
//
// An affiliate link looks like https://shakespeareai.net/?ref=AFF1866765D3A.
// Nothing previously read that parameter, so a click could never become an
// attributed signup and the affiliate dashboard showed permanent zeros.
//
// The code is stashed in localStorage on the first page view that carries it
// and replayed at registration, because signup usually happens on a later
// page (or a later session) than the click.

const KEY = 'sa_affiliate_ref';
// 90 days to match what /affiliate and the support assistant already promise
// ("90-day cookie tracking window"). Any change here must change that copy too.
const WINDOW_DAYS = 90;

type Stored = { code: string; ts: number };

function read(): Stored | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Stored;
    if (!parsed?.code || typeof parsed.ts !== 'number') return null;
    const ageDays = (Date.now() - parsed.ts) / 86400000;
    if (ageDays > WINDOW_DAYS) {
      localStorage.removeItem(KEY);
      return null;
    }
    return parsed;
  } catch {
    // Private mode / blocked storage — attribution is best-effort, never fatal.
    return null;
  }
}

/**
 * Capture ?ref= from the current URL, if present, and ping the click tracker.
 * First touch wins: an existing stored code is not overwritten, so an
 * affiliate who genuinely introduced someone doesn't lose credit to a link
 * clicked later.
 */
export function captureAffiliateRef(apiBaseUrl: string): void {
  try {
    const code = new URLSearchParams(window.location.search).get('ref');
    if (!code) return;
    const clean = code.trim().toUpperCase();
    if (!/^[A-Z0-9]{4,32}$/.test(clean)) return;

    const existing = read();
    if (existing) return;

    localStorage.setItem(KEY, JSON.stringify({ code: clean, ts: Date.now() }));

    // Fire-and-forget: a failed click ping must never block the page.
    fetch(`${apiBaseUrl}/affiliate/track-click`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ aff_code: clean }),
    }).catch(() => {});
  } catch {
    /* no-op */
  }
}

/** The stored referral code, or null if there isn't a live one. */
export function getAffiliateRef(): string | null {
  return read()?.code ?? null;
}

/** Called once attribution has been persisted server-side. */
export function clearAffiliateRef(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* no-op */
  }
}
